import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEOHead Component
 * Dynamically manages SEO meta tags based on environment
 * 
 * Production (www.airflynow.com):
 * - Allows indexing
 * - Sets canonical URLs
 * - Enables sitemap
 * 
 * Development (dev.airflynow.com):
 * - Blocks search engine indexing
 * - Canonical points to production
 * - No sitemap
 */
function SEOHead() {
  const location = useLocation();
  const environment = process.env.REACT_APP_ENVIRONMENT || 'development';
  const canonicalBaseUrl = process.env.REACT_APP_CANONICAL_URL || 'https://www.airflynow.com';
  const isProduction = environment === 'production';

  useEffect(() => {
    // Remove existing meta tags we'll manage
    const existingRobots = document.querySelector('meta[name="robots"]');
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    
    if (existingRobots) existingRobots.remove();
    if (existingCanonical) existingCanonical.remove();

    // Add robots meta tag
    const robotsMeta = document.createElement('meta');
    robotsMeta.name = 'robots';
    robotsMeta.content = isProduction ? 'index, follow' : 'noindex, nofollow';
    document.head.appendChild(robotsMeta);

    // Add canonical link
    const canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    
    // Build canonical URL based on current path
    const currentPath = location.pathname;
    const canonicalUrl = `${canonicalBaseUrl}${currentPath === '/' ? '' : currentPath}`;
    canonicalLink.href = canonicalUrl;
    document.head.appendChild(canonicalLink);

    // Cleanup on unmount
    return () => {
      if (robotsMeta.parentNode) robotsMeta.remove();
      if (canonicalLink.parentNode) canonicalLink.remove();
    };
  }, [location.pathname, isProduction, canonicalBaseUrl]);

  // Add environment indicator for dev
  useEffect(() => {
    if (!isProduction) {
      const existingIndicator = document.getElementById('env-indicator');
      if (existingIndicator) existingIndicator.remove();

      const indicator = document.createElement('div');
      indicator.id = 'env-indicator';
      indicator.style.cssText = `
        position: fixed;
        bottom: 10px;
        right: 10px;
        background: #ff6b00;
        color: white;
        padding: 4px 10px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 700;
        z-index: 999999;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        font-family: monospace;
        pointer-events: none;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      `;
      indicator.textContent = `${environment} • NOT INDEXED`;
      document.body.appendChild(indicator);

      return () => {
        const el = document.getElementById('env-indicator');
        if (el) el.remove();
      };
    }
  }, [isProduction, environment]);

  return null; // This component doesn't render anything
}

export default SEOHead;
